import React, { useContext, useEffect, useState } from "react";
import { useStyles } from "./style";
import { Paper, IconButton } from "@mui/material";
import { Box } from "@mui/system";
import { Grid, Typography, Button, Divider, TextField, MenuItem } from "@mui/material";
import AppSearchBar from "../../../Components/AppSearchBar";
import svg8 from "../../../Assets/svg8.svg";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessCardContainer from "./BusinessCardContainer";
import BusinessDetailPage from "./BusinessDetail";
import { useNavigate, useLocation } from "react-router-dom";
import routes from "../../../Utils/routes";
import { getBusinesses } from "../../../Utils/apiService";
import { AuthUser } from "../../../interfaces/Contexts";
import { AuthContext } from "../../../Helpers/Contexts/authContext";

// Define interface for business data
interface BusinessData {
    Id: string;
    Name: string;
    Description?: string;
    Business_Type__c?: string;
    Phone?: string;
    Business_Email__c?: string;
    Hide_Business_Email__c?: boolean;
    Hide_Business_Phone__c?: boolean;
    Website?: string;
    Business_Address__c?: string;
    Location_URL__c?: string;
    Cover_Image_Url__c?: string;
    Logo_URL__c?: string;
    Is_Active__c?: boolean;
    Business_Approval__c?: string;
    RecordTypeId?: string;
    Business_Directories__r?: {
        records: Array<{
            Contact__r: {
                Name?: string;
                Profile_Picture_URL__c?: string;
            }
        }>
    };
    attributes?: {
        type: string;
        url: string;
    };
}

// Define interface for card data
interface CardData {
    id: string;
    businessImage: string;
    logo: string;
    category: string;
    name: string;
    location: string;
    contactName: string;
    contactImage: string;
}

// Define SpeechRecognition for TypeScript
interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
    readonly length: number;
    [index: number]: SpeechRecognitionResult;
    item(index: number): SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
    readonly length: number;
    [index: number]: SpeechRecognitionAlternative;
    item(index: number): SpeechRecognitionAlternative;
    isFinal: boolean;
}

interface SpeechRecognitionAlternative {
    transcript: string;
    confidence: number;
}

interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    grammars: SpeechGrammarList;
    interimResults: boolean;
    lang: string;
    maxAlternatives: number;
    onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
    onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
    onend: ((this: SpeechRecognition, ev: Event) => any) | null;
    onerror: ((this: SpeechRecognition, ev: Event) => any) | null;
    onnomatch: ((this: SpeechRecognition, ev: Event) => any) | null;
    onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
    onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
    onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
    onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
    onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
    onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

declare global {
    interface Window {
        SpeechRecognition: new () => SpeechRecognition;
        webkitSpeechRecognition: new () => SpeechRecognition;
    }
}
const useMediaQuery = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 600);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
};
const BusinessDirectory: React.FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const navigate = useNavigate();
    const { state: { user } } = useContext(AuthContext);
    const userData = user as AuthUser;
    const [localSearchInput, setLocalSearchInput] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');
    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
    const [notificationOpen, setNotificationOpen] = useState<boolean>(false);
    const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
    const [view, setView] = useState<'list' | 'detail'>('list');
    const [selectedFilterType, setSelectedFilterType] = useState<string>("");
    const [selectedLocation, setSelectedLocation] = useState<string>("");

    // State for API data
    const [myBusinesses, setMyBusinesses] = useState<BusinessData[]>([]);
    const [allBusinesses, setAllBusinesses] = useState<BusinessData[]>([]);
    const [selectedBusinessData, setSelectedBusinessData] = useState<BusinessData | null>(null);
    const [businessDirectoryTypes, setBusinessDirectoryTypes] = useState<Array<{ Name: string, Id: string }>>([]);
    const [expressedBusinessInterests, setExpressedBusinessInterests] = useState<any[]>([]);
    const isMobile = useMediaQuery();

    const [businessSource, setBusinessSource] = useState<'all' | 'my'>('all');

    let recognition: SpeechRecognition | null = null;

    // Add state handling from navigation
    useEffect(() => {
        if (location.state) {
            const { selectedBusinessData, selectedBusinessId, view, businessSource } = location.state as any;
            if (selectedBusinessData && selectedBusinessId && view === 'detail') {
                setSelectedBusinessData(selectedBusinessData);
                setSelectedBusinessId(selectedBusinessId);
                setView('detail');
                setBusinessSource(businessSource);
            }
        }
    }, [location.state]);

    const getStatusStyle = (status: string): { bgcolor: string; color: string } => {
        switch (status) {
            case "Approved":
                return { bgcolor: "#D5ECD2", color: "#1F4B2E" };
            case "Pending":
                return { bgcolor: "#FFF4D6", color: "#7A6000" };
            case "Rejected":
                return { bgcolor: "#FCD8D6", color: "#8B2D2B" };
            case "Inactive":
                return { bgcolor: "#E4E4E7", color: "#3F3F46" };
            default:
                return { bgcolor: "#E4E4E7", color: "#3F3F46" };
        }
    };
    const getUrlParameter = (name: string) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    };
    const businessid = getUrlParameter('businessid');
    console.log(businessid, "businessid from url");

    // Function to fetch businesses
    const fetchBusinesses = async () => {
        try {
            const contactId = userData?.applicantId;
            const response = await getBusinesses(contactId);
            if (response && response.data && response.data.data) {
                const { myBusinesses, allBusinesses, businessDirectoryTypes, expressedBusinessInterests } = response.data.data;
                setMyBusinesses(myBusinesses || []);
                setAllBusinesses(allBusinesses || []);
                setBusinessDirectoryTypes(businessDirectoryTypes || []);
                setExpressedBusinessInterests(expressedBusinessInterests || []);
                return allBusinesses || [];
            }
            
        } catch (error) {
            console.error("Error fetching businesses:", error);
            return [];
        }
    };

    useEffect(() => {
        fetchBusinesses();
    }, []);

    const handleMicHold = (): void => {
        setNotificationOpen(true);
        if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
            recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
            recognition.lang = 'en-US';
            recognition.onstart = () => {
                console.log('Speech recognition started');
            };
            recognition.onresult = (event: SpeechRecognitionEvent) => {
                const speechResult = event.results[0][0].transcript;
                setSearchInput(speechResult);
            };
            recognition.onerror = (event: any) => {
                console.error('Speech recognition error:', event.error);
            };
            recognition.onnomatch = (event: any) => {
                console.warn('No speech recognized:', event);
            };
            recognition.onspeechend = (event: any) => {
                console.log('Speech recognition ended:', event);
            };
            recognition.onend = () => {
                console.log('Speech recognition stopped');
                setNotificationOpen(false);
            };
            recognition.start();
        } else {
            console.log('Speech recognition not supported');
        }
    };

    const handleMicRelease = (): void => {
        if (recognition) {
            recognition.stop();
        }
    };

    const toggleFilter = (): void => {
        setIsFilterOpen(true);
    };

    const closeFilter = (): void => {
        setIsFilterOpen(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setLocalSearchInput(event.target.value);
        setSearchInput(event.target.value);
    };

    const clearFilters = (): void => {
        setLocalSearchInput('');
        setSearchInput('');
        setSelectedFilterType("");
        setSelectedLocation("");
        setIsFilterOpen(false);
    };

    const applyFilters = (): void => {
        setIsFilterOpen(false);
    };

    useEffect(() => {
        if (businessid) {
            const business = myBusinesses.find(b => b.Id === businessid);
            if (business) {
                setSelectedBusinessData(business);
                setSelectedBusinessId(businessid);
                setView('detail');
                setBusinessSource('all'); // Assuming businessid is from all businesses
                console.log("Business found with ID:", businessid);
            } else {
                console.error("Business not found with ID:", businessid);   }
        }
    }, [businessid, allBusinesses]);
    const handleBusinessCardClick = (businessId: string, source: 'all' | 'my' = 'all'): void => {
        setBusinessSource(source);

        // Debug logs to check what's happening
        console.log("Clicked business ID:", businessId);
        console.log("Source:", source);
        console.log("My businesses:", myBusinesses);
        console.log("All businesses:", allBusinesses);

        const selectedBusiness = source === 'my'
            ? myBusinesses.find(business => business.Id === businessId)
            : allBusinesses.find(business => business.Id === businessId);

        console.log("Selected business:", selectedBusiness);

        if (selectedBusiness) {
            setSelectedBusinessData(selectedBusiness);
            setSelectedBusinessId(businessId);
            setView('detail');

            if (isMobile) {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }

            navigate(`/business directory`, {
                state: {
                    businessName: selectedBusiness.Name,
                    businessSource: source
                }
            });
        } else {
            console.error("Business not found with ID:", businessId);
        }
    };

    const handleBackToList = (): void => {
        setSelectedBusinessId(null);
        setSelectedBusinessData(null);
        setView('list');
    };

    const distinctTypes = Array.from(
        new Set(allBusinesses.map(business => business.Business_Type__c).filter(Boolean))
    ) as string[];

    const filteredBusinesses = allBusinesses.filter(business => {
        const matchesSearch = searchInput.trim() === "" ||
            business.Name.toLowerCase().includes(searchInput.trim().toLowerCase());

        const matchesType = selectedFilterType === "" ||
            business.Business_Type__c === selectedFilterType;

        const matchesLocation = selectedLocation === "" ||
            (business.Business_Address__c &&
                business.Business_Address__c.toLowerCase().includes(selectedLocation.toLowerCase()));

        return matchesSearch && matchesType && matchesLocation;
    });

    const transformedBusinessCards: CardData[] = filteredBusinesses.map(business => {
        const defaultContact = {
            name: "Unknown Contact",
            image: "https://thumbs.dreamstime.com/b/creative-illustration-default-avatar-profile-placeholder-isolated-background-art-design-grey-photo-blank-template-mockup-144855718.jpg"
        };

        let contactInfo = defaultContact;
        if (business.Business_Directories__r && business.Business_Directories__r.records.length > 0) {
            const firstContact = business.Business_Directories__r.records[0].Contact__r;
            contactInfo = {
                name: firstContact.Name || defaultContact.name,
                image: firstContact.Profile_Picture_URL__c || defaultContact.image
            };
        }

        return {
            id: business.Id,
            businessImage: business.Cover_Image_Url__c || "https://ample.co.in/wp-content/uploads/2023/12/img-main.jpg",
            logo: business.Logo_URL__c || "https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg",
            category: business.Business_Type__c || "No Category",
            name: business.Name,
            location: business.Business_Address__c || "No Location",
            contactName: contactInfo.name,
            contactImage: contactInfo.image
        };
    });

    const refreshSelectedBusinessData = async () => {
        await fetchBusinesses();
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={8.5} lg={8.5} xl={8.5}>
                <Paper style={{ boxShadow: 'none', height: '100%' }} className={`${classes.cardContainer1}`}>
                    {view === 'list' ? (
                        <>
                            <Grid style={{ marginBottom: 10 }} container display="flex" alignItems="center" justifyContent="space-between">
                                <Grid item xs={12} md={5} lg={5} xl={5}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography
                                            sx={{
                                                fontSize: '15px',
                                                color: '#373A45',
                                                fontWeight: '500',
                                                textAlign: 'start',
                                                '@media (max-width: 600px)': {
                                                    fontSize: '14px !important',
                                                },
                                            }}
                                        >
                                            {'All businesses'}
                                        </Typography>
                                        {isMobile && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    onClick={() => navigate(routes.createbusinessdirectory)}
                                                    sx={{
                                                        background: '#2C60AA',
                                                        color: '#fff',
                                                        textTransform: 'none',
                                                        fontWeight: 500,
                                                        fontSize: '12px',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        '&:hover': {
                                                                background: '#2C60AA',
                                                        color: '#fff',
                                                        }
                                                    }}
                                                >
                                                    List your Business
                                                </Button>
                                                {filteredBusinesses.length > 2 && (
                                                    <Button
                                                        onClick={() => navigate(routes.allBusinesses, {
                                                            state: {
                                                                businesses: allBusinesses,
                                                                title: "All Businesses",
                                                                searchInput: searchInput,
                                                                selectedFilterType: selectedFilterType,
                                                                selectedLocation: selectedLocation
                                                            }
                                                        })}
                                                        sx={{
                                                            background: '#2C60AA',
                                                            color: '#fff',
                                                            textTransform: 'none',
                                                            fontWeight: 500,
                                                            fontSize: '12px',
                                                            padding: '6px 12px',
                                                            borderRadius: '8px',
                                                            '&:hover': {
                                                                  background: '#2C60AA',
                                                            color: '#fff',
                                                            }
                                                        }}
                                                    >
                                                        View more
                                                    </Button>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={7} lg={7} xl={7}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                            <AppSearchBar
                                                handleMicHold={handleMicHold}
                                                handleMicRelease={handleMicRelease}
                                                localSearchInput={localSearchInput}
                                                handleChange={handleChange}
                                                openSnackbar={notificationOpen}
                                            />
                                        </Grid>
                                        {/* <Grid item xs={3} sm={3} md={2} lg={2} xl={2}>
                                            <Button
                                                style={{
                                                    color: "#373A45",
                                                    backgroundColor: "#E8EBF3",
                                                    borderRadius: 5,
                                                    padding: "7px 13px"
                                                }}
                                                startIcon={<FilterAltOutlinedIcon style={{ color: "#373A45" }} />}
                                                onClick={toggleFilter}
                                            >
                                                Filter
                                            </Button>
                                        </Grid> */}
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* Filter Modal */}
                            {isFilterOpen && (
                                <Grid
                                    style={{
                                        zIndex: "999",
                                        position: "relative",
                                        width: "100%"
                                    }}
                                >
                                    <Paper
                                        className={`filter-modal ${classes.cardContainer2} ${isMobile ? classes.filterModalMobile : ''}`}
                                        style={
                                            isMobile
                                                ? {
                                                    position: "absolute",
                                                    right: "0",
                                                    top: "100%",
                                                    backgroundColor: "white",
                                                    padding: "0px",
                                                    width: "100%",
                                                    zIndex: "999",
                                                    border: "1px solid #E4E4E7",
                                                    borderRadius: "12px",
                                                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                                                    overflowY: "scroll",
                                                    marginTop: "10px",
                                                    transform: "none",
                                                    transition: "none"
                                                }
                                                : {
                                                    position: "absolute",
                                                    right: "0",
                                                    backgroundColor: "white",
                                                    padding: "0px",
                                                    width: "360px",
                                                    zIndex: "999",
                                                    border: "1px solid #E4E4E7",
                                                    borderRadius: "12px",
                                                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                                                    overflowY: "scroll",
                                                }
                                        }
                                    >
                                        <Box p={3} pb={2}>
                                            <Grid container>
                                                <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                                                    <Typography variant="h6" style={{ fontWeight: "600", fontSize: "18px" }}>
                                                        Filters
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} style={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <IconButton onClick={closeFilter} size="small">
                                                        <CloseIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <Box px={3} pb={3}>
                                            <Box mb={2}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    placeholder="Location"
                                                    value={selectedLocation}
                                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                                    variant="outlined"
                                                    SelectProps={{
                                                        displayEmpty: true,
                                                        renderValue: (selected) => {
                                                            return selected ? String(selected) : "Location";
                                                        }
                                                    }}
                                                    InputProps={{
                                                        style: {
                                                            height: "36px",
                                                            border: "0.5px solid #B2B3B7",
                                                            borderRadius: "8px"
                                                        }
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                border: '0.5px solid #B2B3B7',
                                                                borderRadius: '8px',
                                                            },
                                                            '&:hover fieldset': {
                                                                border: '0.5px solid #B2B3B7',
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            height: '36px',
                                                            paddingTop: '0',
                                                            paddingBottom: '0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>All Locations</em>
                                                    </MenuItem>
                                                    {Array.from(new Set(allBusinesses
                                                        .map(business => business.Business_Address__c)
                                                        .filter(Boolean)))
                                                        .map((location) => (
                                                            <MenuItem key={location} value={location}>
                                                                {location}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </Box>

                                            <Box mb={2}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    placeholder="Business type"
                                                    value={selectedFilterType}
                                                    onChange={(e) => setSelectedFilterType(e.target.value)}
                                                    variant="outlined"
                                                    SelectProps={{
                                                        displayEmpty: true,
                                                        renderValue: (selected) => {
                                                            return selected ? String(selected) : "Business type";
                                                        }
                                                    }}
                                                    InputProps={{
                                                        style: {
                                                            height: "36px",
                                                            border: "0.5px solid #B2B3B7",
                                                            borderRadius: "8px"
                                                        }
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                border: '0.5px solid #B2B3B7',
                                                                borderRadius: '8px',
                                                            },
                                                            '&:hover fieldset': {
                                                                border: '0.5px solid #B2B3B7',
                                                            },
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            height: '36px',
                                                            paddingTop: '0',
                                                            paddingBottom: '0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>All Business Types</em>
                                                    </MenuItem>
                                                    {distinctTypes.map((type) => (
                                                        <MenuItem key={type} value={type}>
                                                            {type}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Box>

                                            <Box
                                                mt={8}
                                                display="flex"
                                                justifyContent="space-between"
                                                style={{
                                                    position: "absolute",
                                                    bottom: "20px",
                                                    left: "0",
                                                    right: "0",
                                                    padding: "0 24px"
                                                }}
                                            >
                                                <Button
                                                    onClick={clearFilters}
                                                    style={{
                                                        borderRadius: "8px",
                                                        color: "#111827",
                                                        backgroundColor: "white",
                                                        padding: "8px 16px",
                                                        width: "45%",
                                                        textTransform: "none",
                                                        fontWeight: "500"
                                                    }}
                                                >
                                                    Reset all filters
                                                </Button>
                                                <Button
                                                    onClick={applyFilters}
                                                    style={{
                                                       background: '#2C60AA',
                                                            color: '#fff',
                                                        padding: "8px 16px",
                                                        width: "45%",
                                                        textTransform: "none",
                                                        fontWeight: "500"
                                                    }}
                                                >
                                                    Show results
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                            <Grid className={`scrollbar-cusgray ${classes.cardContainer}`} style={{ height: "71.5vh", overflowY: "scroll", scrollbarWidth: "none" }}>
                                <BusinessCardContainer
                                    cardData={isMobile ? transformedBusinessCards.slice(0, 2) : transformedBusinessCards}
                                    onCardClick={handleBusinessCardClick}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid style={{ marginBottom: 10 }} container display="flex" alignItems="center">
                                <Grid item>
                                <Button
  startIcon={<ArrowBackIcon style={{ color: '#fff' }} />}
  onClick={handleBackToList}
  sx={{
    backgroundColor: '#2C60AA',
    color: '#fff',
    fontWeight: 500,
    mb: 2,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#2C60AA',
      color: '#fff',
    },
  }}
>
  Back to All Businesses
</Button>

                                </Grid>
                            </Grid>
                            <Grid className={`scrollbar-cusgray ${classes.cardContainer}`}>
                                <BusinessDetailPage
                                    businessData={selectedBusinessData as any}
                                    businessDirectoryTypes={businessDirectoryTypes}
                                    expressedBusinessInterests={expressedBusinessInterests}
                                    onBackClick={handleBackToList}
                                    businessSource={businessSource}
                                    onBusinessDeleted={fetchBusinesses}
                                    isMobile={window.innerWidth <= 600}
                                    onPromotionSubmitted={refreshSelectedBusinessData}
                                />
                            </Grid>
                        </>
                    )}
                </Paper>
            </Grid>
            <Grid item xs={12} sm={12} md={3.5} lg={3.5} xl={3.5} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Grid
                    sx={{
                        backgroundColor: "white",
                        borderRadius: "9px",
                        padding: "15px",
                        textAlign: "start",
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        height: "44vh",
                        overflowY: "scroll",
                        scrollbarWidth: "none"
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight={"500"} color={"#373A45"}>My businesses</Typography>
                        {isMobile && myBusinesses.length > 0 && (
                            <Button
                                onClick={() => navigate(routes.myBusinesses, {
                                    state: {
                                        businesses: myBusinesses,
                                        title: "My Businesses"
                                    }
                                })}
                                sx={{
                                 background: '#2C60AA',
                                                            color: '#fff',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    minWidth: 'auto',
                                    padding: '6px 16px',
                                    borderRadius: '8px',
                                    '&:hover': {
                                         background: '#2C60AA',
                                                            color: '#fff',
                                    }
                                }}
                            >
                                View more
                            </Button>
                        )}
                    </Box>
                    <hr style={{ opacity: "0.10", color: "black" }} />
                    <Box
                        className="scrollbar-cusgray"
                        sx={{
                            overflowY: 'auto',
                            paddingRight: '5px',
                            flexGrow: 1,
                        }}
                    >
                        <Grid container direction="column" spacing={2}>
                            {myBusinesses.length > 0 ? (
                                (isMobile ? myBusinesses.slice(0, 2) : myBusinesses).map((item, index) => (
                                    <Grid key={index} item onClick={() => {
                                        handleBusinessCardClick(item.Id, 'my');

                                        if (isMobile) {
                                            setTimeout(() => {
                                                window.scrollTo({
                                                    top: 0,
                                                    behavior: 'smooth'
                                                });
                                            }, 100);
                                        }
                                    }} style={{ cursor: 'pointer' }}>
                                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#F8F9FC' }}>
                                            <Grid container justifyContent="space-between" alignItems={"center"} columnSpacing={3}>
                                                <Grid item xs={3} >
                                                    <Box
                                                        component="img"
                                                        src={item.Logo_URL__c || "https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg"}
                                                        alt="logo"
                                                        alignItems={"center"}
                                                        sx={{
                                                            width: 68,
                                                            height: 68,
                                                            objectFit: 'fill',
                                                            borderRadius: '50%',
                                                            bgcolor: '#fff'
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={5}>
                                                    <Typography fontSize={"14px"} fontWeight={500}>
                                                        {item.Name}
                                                    </Typography>
                                                    <Typography fontSize={"12px"} color="text.secondary" mt={0.5}>
                                                        <LocationOnOutlinedIcon style={{ fontSize: "18px" }} /> {item.Business_Address__c || "No Location"}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        sx={{ mt: 1, borderRadius: '20px', fontSize: "10px", textTransform: 'none', color: "#373A45", border: "1px solid #D9DBE1" }}
                                                    >
                                                        {item.Business_Type__c || "No Category"}
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={4} textAlign="right" alignSelf="flex-start">
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            ...getStatusStyle(item.Business_Approval__c || ""),
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: '20px',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {item.Business_Approval__c || "Pending"}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                ))
                            ) : (
                                <Typography textAlign="center" color="text.secondary" mt={2}>
                                    No businesses found
                                </Typography>
                            )}
                        </Grid>
                    </Box>
                </Grid>

                <Grid
                    className={classes.ShowcaseYourBusiness}
                    sx={{
                        cursor: "pointer",
                        padding: 2,
                        textAlign: "center",
                        borderRadius: 2,
                        backgroundColor: "#2C60AA",
                        display: { xs: "none", sm: "flex" },
                        flexDirection: "column",
                        justifyContent: "space-between",
                        minHeight: { xs: 200, sm: 300 },
                    }}
                    onClick={() => {
                        navigate(routes.createbusinessdirectory, {
                            state: { breadCrumbs: "List your business" },
                        });
                    }}
                >
                    {/* Top: Title + Subtitle */}
                    <Box>
                        <Typography fontWeight={500}>Showcase your business</Typography>
                        <Typography color={"#fff"}>
                            Promote your venture to fellow alumni.
                        </Typography>
                    </Box>

                    {/* Center Image */}
                    <Box display="flex" justifyContent="center" mt={2}>
                        <img
                            src={svg8}
                            alt="Business"
                            style={{ width: "100px", height: "100px", borderRadius: "9px" }}
                        />
                    </Box>

                    {/* Bottom Divider + Text & Icon Row */}
                    <Box mt={3}>
                        <hr style={{ opacity: 0.3, marginBottom: 8 }} />
                        <Grid container alignItems="center">
                            <Grid item xs={6} textAlign="left">
                                <Typography fontSize={14}>List your business</Typography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                                <ArrowForwardIcon />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default BusinessDirectory;
